import { axiosRubyClient } from '@/util/axios/axiosClient';

// Preferred-worker pool management (GRAVY-10265). Tenant-admin only (P10);
// every endpoint 404s while PREFERRED_WORKER_POOLS_ENABLED is unset (P13),
// which listPools translates into { enabled: false } so the UI renders nothing.

export type PoolHealth = 'red' | 'amber' | 'green';
export type PoolTier = 'monitored' | 'standard' | 'gold' | 'platinum';
export type NominationSource = 'client_nomination' | 'system_suggestion';

export interface PoolSkill {
  id: number;
  name: string | null;
}

export interface PoolRow {
  id: number;
  skill: PoolSkill;
  active_member_count: number;
  target_depth: number;
  alert_threshold: number;
  health: PoolHealth;
  pending_nominations_count: number;
  rookie_slots: number;
  rookie_slots_filled: number;
  status: 'active' | 'archived';
}

export interface PoolDetail extends PoolRow {
  customer: { id: number; name: string | null };
}

export interface PoolWorker {
  id: number;
  name: string | null;
  avatar_url: string | null;
}

interface PoolWorkerBlock {
  worker: PoolWorker;
  tier: PoolTier | null;
  tier_source: 'dependability_v2' | 'none';
  rookie: boolean;
  shifts_in_skill_total: number;
  shifts_with_client: number;
}

// GRAVY-10394: the one-time backfill review returned by createPool — workers
// with a qualifying 5★ for this skill from before the pool existed. Never
// persisted server-side; ignoring the review is final by construction.
export interface BackfillCandidate extends PoolWorkerBlock {
  qualifying_rating_count: number;
  last_rated_at: string | null;
}

export interface BackfillReview {
  window: 'full_history' | '90_days';
  candidates: BackfillCandidate[];
}

export interface PoolMemberRow extends PoolWorkerBlock {
  membership_id: number;
  worker_active: boolean;
  // GRAVY-10395: staffing readiness — optional so a frontend deployed ahead
  // of graphy tolerates the fields' absence.
  worker_ready_to_work?: boolean;
  not_ready_reason?: string | null;
  source: string;
  added_at: string | null;
}

export interface ClientNominationReview {
  timesheet_id: number | null;
  job_id: number | null;
  shift_start_at: string | null;
  account_name: string | null;
  rating: number | null;
  rated_at: string | null;
  rated_by: 'client' | 'internal' | null;
}

export interface SystemNominationReview {
  qualifying_shift_count: number | null;
  qualifying_score: number | null;
  skill_name: string | null;
  market_name: string | null;
}

export interface PoolNominationRow extends PoolWorkerBlock {
  nomination_id: number;
  source: NominationSource;
  occurred_at: string | null;
  review: ClientNominationReview | SystemNominationReview;
  also_qualified: Record<string, unknown> | null;
}

export interface PoolPagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface PoolsListResult {
  enabled: boolean;
  pools: PoolRow[];
}

export interface PageParams {
  page?: number;
  per_page?: number;
}

export interface CreatePoolParams {
  skill_id: number;
  target_depth?: number;
  alert_threshold?: number;
}

export interface UpdatePoolParams {
  target_depth?: number;
  alert_threshold?: number;
  status?: 'archived';
}

interface ApiErrorShape {
  isAxiosError?: boolean;
  response?: { status?: number; data?: { errors?: string[] } };
}

const gatedStatus = (error: unknown): boolean => {
  const status = (error as ApiErrorShape)?.response?.status;
  return status === 404 || status === 403;
};

export const extractPoolsError = (error: unknown, fallback: string): string => {
  const errors = (error as ApiErrorShape)?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(', ');
  }
  return fallback;
};

export class PreferredPoolsAPI {
  static async listPools(
    customerId: number | string,
  ): Promise<PoolsListResult> {
    try {
      const response = await axiosRubyClient.get(
        `/api/admin/customers/${customerId}/preferred_pools`,
      );
      return response.data.data as PoolsListResult;
    } catch (error) {
      if (gatedStatus(error)) return { enabled: false, pools: [] };
      throw error;
    }
  }

  static async createPool(
    customerId: number | string,
    params: CreatePoolParams,
  ): Promise<PoolRow & { backfill_review?: BackfillReview }> {
    const response = await axiosRubyClient.post(
      `/api/admin/customers/${customerId}/preferred_pools`,
      params,
    );
    return response.data.data as PoolRow & { backfill_review?: BackfillReview };
  }

  static async getPool(poolId: number): Promise<PoolDetail> {
    const response = await axiosRubyClient.get(
      `/api/admin/preferred_pools/${poolId}`,
    );
    return response.data.data as PoolDetail;
  }

  static async updatePool(
    poolId: number,
    params: UpdatePoolParams,
  ): Promise<PoolDetail> {
    const response = await axiosRubyClient.patch(
      `/api/admin/preferred_pools/${poolId}`,
      params,
    );
    return response.data.data as PoolDetail;
  }

  static async listMembers(
    poolId: number,
    params: PageParams = {},
  ): Promise<{ members: PoolMemberRow[]; pagination: PoolPagination }> {
    const response = await axiosRubyClient.get(
      `/api/admin/preferred_pools/${poolId}/members`,
      { params },
    );
    return {
      members: response.data.data as PoolMemberRow[],
      pagination: response.data.pagination as PoolPagination,
    };
  }

  static async addMember(
    poolId: number,
    params: { worker_id: number; reason?: string; source?: 'backfill_review' },
  ): Promise<PoolMemberRow> {
    const response = await axiosRubyClient.post(
      `/api/admin/preferred_pools/${poolId}/members`,
      params,
    );
    return response.data.data as PoolMemberRow;
  }

  static async removeMember(
    poolId: number,
    membershipId: number,
    reason?: string,
  ): Promise<void> {
    await axiosRubyClient.delete(
      `/api/admin/preferred_pools/${poolId}/members/${membershipId}`,
      { data: { reason } },
    );
  }

  static async listNominations(
    poolId: number,
    params: PageParams = {},
  ): Promise<{ nominations: PoolNominationRow[]; pagination: PoolPagination }> {
    const response = await axiosRubyClient.get(
      `/api/admin/preferred_pools/${poolId}/nominations`,
      { params },
    );
    return {
      nominations: response.data.data as PoolNominationRow[],
      pagination: response.data.pagination as PoolPagination,
    };
  }

  static async approveNomination(
    poolId: number,
    nominationId: number,
    note?: string,
  ): Promise<PoolMemberRow> {
    const response = await axiosRubyClient.post(
      `/api/admin/preferred_pools/${poolId}/nominations/${nominationId}/approve`,
      note ? { note } : {},
    );
    return response.data.data as PoolMemberRow;
  }

  static async declineNomination(
    poolId: number,
    nominationId: number,
    reason?: string,
  ): Promise<void> {
    await axiosRubyClient.post(
      `/api/admin/preferred_pools/${poolId}/nominations/${nominationId}/decline`,
      reason ? { reason } : {},
    );
  }
}

export const isClientReview = (
  review: ClientNominationReview | SystemNominationReview,
  source: NominationSource,
): review is ClientNominationReview => source === 'client_nomination';
